import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/api';
import './AIChatBubble.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatBubbleProps {
  /** true = trang đặt lịch công cộng, false = trang quản trị */
  isPublic?: boolean;
  /** Mã booking của salon (chỉ cần khi isPublic=true) */
  bookingCode?: string;
  /** Ngôn ngữ hiện tại: 'vi' hoặc 'en' */
  language?: string;
}

// ── Quick questions theo context ──────────────────────────────────────
const quickQuestionsAdmin = {
  vi: [
    '📅 Hôm nay có bao nhiêu lịch hẹn?',
    '👥 Nhân viên nào đang làm việc hôm nay?',
    '💰 Doanh thu tháng này bao nhiêu?',
    '⭐ Khách hàng đánh giá mới nhất?',
  ],
  en: [
    '📅 How many bookings are there today?',
    '👥 Which staff are working today?',
    '💰 What is the revenue this month?',
    '⭐ What are the latest customer reviews?',
  ]
};

const quickQuestionsPublic = {
  vi: [
    '🕐 Salon mở cửa mấy giờ?',
    '✂️ Salon có những dịch vụ gì?',
    '📍 Địa chỉ salon ở đâu?',
    '👩 Thợ Lisa ngày mai có rảnh không?',
  ],
  en: [
    '🕐 What are the salon opening hours?',
    '✂️ What services does the salon offer?',
    '📍 Where is the salon located?',
    '👩 Is Lisa available tomorrow?',
  ]
};

const AIChatBubble: React.FC<AIChatBubbleProps> = ({
  isPublic = false,
  bookingCode = '',
  language = 'vi'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const lang = language === 'en' ? 'en' : 'vi';
  const quickQuestions = isPublic ? quickQuestionsPublic[lang] : quickQuestionsAdmin[lang];

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 350);
    }
  }, [isOpen]);

  const handleOpen = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 240);
  }, []);

  const toggleChat = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleClose, handleOpen]);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    const userMessage: Message = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build history (exclude the new message we just added)
      const history = messages.map(m => ({ role: m.role, content: m.content }));

      let result: any;
      if (isPublic && bookingCode) {
        result = await apiService.chatWithPublicAI(bookingCode, trimmed, history);
      } else {
        result = await apiService.chatWithAdminAI(trimmed, history);
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: result.reply || (lang === 'vi' ? 'Xin lỗi, tôi không thể trả lời lúc này.' : 'Sorry, I cannot answer right now.')
      };
      setMessages(prev => [...prev, assistantMessage]);

      // If chat is closed, show badge
      if (!isOpen) setHasUnread(true);

    } catch (err: any) {
      const errMsg = err.message || (lang === 'vi' ? 'Đã xảy ra lỗi. Vui lòng thử lại.' : 'An error occurred. Please try again.');
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ ${errMsg}` }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, isPublic, bookingCode, lang, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleQuickQuestion = (q: string) => {
    sendMessage(q);
  };

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  const headerName = isPublic
    ? (lang === 'vi' ? 'Trợ lý đặt lịch' : 'Booking Assistant')
    : (lang === 'vi' ? 'Trợ lý AI Salon' : 'Salon AI Assistant');

  const headerStatus = lang === 'vi' ? 'Đang hoạt động' : 'Online';

  const placeholderText = lang === 'vi'
    ? 'Nhắn tin với AI... (Enter để gửi)'
    : 'Message AI... (Enter to send)';

  const welcomeTitle = isPublic
    ? (lang === 'vi' ? 'Xin chào! 👋' : 'Hello there! 👋')
    : (lang === 'vi' ? 'Trợ lý quản lý salon' : 'Salon Management AI');

  const welcomeText = isPublic
    ? (lang === 'vi'
      ? 'Tôi có thể giúp bạn tìm hiểu thông tin về salon, dịch vụ và kiểm tra lịch trống của nhân viên.'
      : 'I can help you learn about the salon, services, and check staff availability.')
    : (lang === 'vi'
      ? 'Tôi có thể giúp bạn quản lý lịch hẹn, kiểm tra doanh thu, nhân viên và nhiều hơn nữa.'
      : 'I can help you manage bookings, check revenue, staff schedules, and much more.');

  // Render via portal to document.body to avoid any parent CSS interference
  // (overflow:hidden, transform, stacking context, etc.)
  const chatUI = (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className={`ai-chat-window ${isClosing ? 'closing' : ''}`} role="dialog" aria-label="AI Assistant">
          {/* Header */}
          <div className="ai-chat-header">
            <div className="ai-chat-avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                <circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/>
                <circle cx="15" cy="13" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </div>
            <div className="ai-chat-header-info">
              <p className="ai-chat-header-name">{headerName}</p>
              <div className="ai-chat-header-status">
                <span className="ai-status-dot" />
                {headerStatus}
              </div>
            </div>
            <button
              className="ai-chat-close-btn"
              onClick={handleClose}
              aria-label="Đóng chat"
              title="Đóng"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="ai-chat-messages">
            {/* Welcome + quick questions when no messages */}
            {messages.length === 0 && (
              <div className="ai-welcome-msg">
                <div className="ai-welcome-icon">🤖</div>
                <h4>{welcomeTitle}</h4>
                <p>{welcomeText}</p>
                <div className="ai-quick-questions">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      className="ai-quick-btn"
                      onClick={() => handleQuickQuestion(q)}
                      disabled={isLoading}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Conversation messages */}
            {messages.map((msg, index) => (
              <div key={index} className={`ai-message ${msg.role}`}>
                <div className="ai-msg-avatar">
                  {msg.role === 'assistant' ? '🤖' : '👤'}
                </div>
                <div className="ai-msg-bubble">
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && (
              <div className="ai-typing">
                <div className="ai-msg-avatar">🤖</div>
                <div className="ai-typing-dots">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Footer */}
          <div className="ai-chat-footer">
            <div className="ai-chat-input-wrap">
              <textarea
                ref={inputRef}
                className="ai-chat-input"
                placeholder={placeholderText}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                rows={1}
              />
            </div>
            <button
              className="ai-chat-send-btn"
              onClick={() => sendMessage(inputValue)}
              disabled={isLoading || !inputValue.trim()}
              aria-label="Gửi tin nhắn"
              title="Gửi"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" stroke="none"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Floating Trigger Button */}
      <button
        className={`ai-chat-trigger ${isOpen ? 'is-open' : ''}`}
        onClick={toggleChat}
        aria-label={isOpen ? 'Đóng trợ lý AI' : 'Mở trợ lý AI'}
        title={isOpen ? 'Đóng' : 'Trợ lý AI'}
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
            <circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/>
            <circle cx="15" cy="13" r="1" fill="currentColor" stroke="none"/>
          </svg>
        )}
        {hasUnread && !isOpen && <span className="ai-chat-trigger-badge" />}
      </button>
    </>
  );

  return createPortal(chatUI, document.body);
};

export default AIChatBubble;
